import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import _ from 'lodash';
import { hotelSearch } from '../store/slices/hotel';
import { setHotelTripSearchInputs } from '../store/slices/tripSearch';
import { HotelPreferences } from '../constants';

const usePreferencesFilter = ({ tripId, doShowInPreferences, doHideRateCodePreferencesFilter }) => {
  const isHotelTripSearch = !tripId;
  const hotelSearchObj = useSelector((store) => (!isHotelTripSearch
    ? (store.hotel.hotelSearches[tripId] || {}).searchObj || {}
    : store.tripSearch.searchInputs.hotel));
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { travelAgency = {} } = useSelector((store) => store.user);
  const {
    doShowOnlyAgentPreferredHotels,
    exactMatchRateCodesOnly,
    doShowCommissionableRatesOnly,
    doShowAvailableHotelsOnly,
    showRefundableHotelRatesOnly,
  } = hotelSearchObj;

  const travelAgencySettings = (travelAgency && travelAgency.settings) || {};
  const travelAgencyFilterSettings = (travelAgency && travelAgency.filterSettings) || {};

  const {
    doShowAgentPreferredHotelCheckbox,
    doShowPreferredRateCodesCheckbox,
    doShowCommissionableRatesCheckbox,
  } = travelAgencySettings;

  const selectedAgentPreferenceValuesPrefix = useMemo(() => {
    const prefixes = [];
    if (doShowAgentPreferredHotelCheckbox && doShowOnlyAgentPreferredHotels) {
      prefixes.push('Agent-preferred hotels');
    }
    if (doShowPreferredRateCodesCheckbox) {
      if (exactMatchRateCodesOnly && doShowInPreferences) {
        prefixes.push('Only show hotels with preferred hotel rate codes');
      } else if (!doShowInPreferences && !exactMatchRateCodesOnly
        && !doHideRateCodePreferencesFilter) {
        prefixes.push('Best Rates');
      }
    }
    if (doShowCommissionableRatesCheckbox && doShowCommissionableRatesOnly) {
      prefixes.push('Commissionable');
    }
    if (showRefundableHotelRatesOnly
      && travelAgencyFilterSettings.doShowRefundableRatesOnlyCheckbox) {
      prefixes.push('Refundable Rates Only');
    }
    if (travelAgencyFilterSettings && travelAgencyFilterSettings.doShowAvailableHotelsOnlyCheckbox
      && doShowAvailableHotelsOnly) {
      prefixes.push('Show Available Properties Only');
    }

    return prefixes.join(', ');
  }, [doShowAgentPreferredHotelCheckbox, doShowAvailableHotelsOnly, doHideRateCodePreferencesFilter,
    doShowCommissionableRatesCheckbox, doShowCommissionableRatesOnly, doShowInPreferences,
    doShowOnlyAgentPreferredHotels, doShowPreferredRateCodesCheckbox, exactMatchRateCodesOnly,
    travelAgencyFilterSettings, showRefundableHotelRatesOnly]);

  const hotelsAgentPreferredCheckboxLabel = travelAgency
    && travelAgency.filterSettings && travelAgency.filterSettings.hotelsAgentPreferredCheckboxLabel;

  const hasRateCodesSelected = useMemo(() => (
    hotelSearchObj && hotelSearchObj.rateCodes && hotelSearchObj.rateCodes.length
  ), [hotelSearchObj]);

  const preferences = useMemo(() => {
    const preferences2 = [];
    if (travelAgency.filterSettings
      && travelAgency.filterSettings.doShowAvailableHotelsOnlyCheckbox) {
      preferences2.push({
        value: HotelPreferences.ONLY_AVAILABLE_HOTELS,
        label: 'Show Available Properties Only',
      });
    }

    if (!travelAgency.settings
      || travelAgency.settings.doShowPreferredRateCodesCheckbox) {
      if (doShowInPreferences) {
        if (hasRateCodesSelected) {
          preferences2.push({
            value: HotelPreferences.ONLY_PREFERED_PARTNER_RATES,
            label: 'Only show hotels with preferred hotel rate codes',
          });
        }
      } else if (!doShowInPreferences && doHideRateCodePreferencesFilter) {
        // If rate codes filter is hidden, we don't want to show best available rates.
      } else {
        preferences2.push({
          value: HotelPreferences.BEST_AVAILABLE_RATES,
          label: 'Show Best Available Rates (non-preferred)',
        });
      }
    }

    if (!travelAgency.settings
      || travelAgency.settings.doShowCommissionableRatesCheckbox) {
      preferences2.push({
        value: HotelPreferences.ONLY_COMMISSIONABLE,
        label: 'Show commissionable rates only',
      });
    }

    if (!travelAgency.settings
      || travelAgency.settings.doShowAgentPreferredHotelCheckbox) {
      preferences2.push({
        value: HotelPreferences.ONLY_AGENT_PREFERRED_HOTEL,
        label: hotelsAgentPreferredCheckboxLabel || 'Only show hotels on agent-preferred hotels list',
      });
    }

    if (travelAgency.filterSettings?.doShowRefundableRatesOnlyCheckbox) {
      preferences2.push({
        value: HotelPreferences.ONLY_REFUNDABLE_RATES,
        label: 'Show Refundable Rates Only',
      });
    }

    return preferences2;
  }, [doShowInPreferences, hotelsAgentPreferredCheckboxLabel, doHideRateCodePreferencesFilter,
    travelAgency.filterSettings, travelAgency.settings, hasRateCodesSelected]);

  function checkPreference(newValue, preference) {
    return newValue && _.find(newValue, (v) => v === preference) !== undefined;
  }

  const handlePreferencesChange = (newValue) => {
    const payload = {
      tripId,
      searchObj: {
        page: 1,
        doShowOnlyAgentPreferredHotels:
          checkPreference(newValue, HotelPreferences.ONLY_AGENT_PREFERRED_HOTEL),
        exactMatchRateCodesOnly: doShowInPreferences
          ? checkPreference(newValue, HotelPreferences.ONLY_PREFERED_PARTNER_RATES)
          : !checkPreference(newValue, HotelPreferences.BEST_AVAILABLE_RATES),
        doShowCommissionableRatesOnly:
          checkPreference(newValue, HotelPreferences.ONLY_COMMISSIONABLE),
        doShowAvailableHotelsOnly:
          checkPreference(newValue, HotelPreferences.ONLY_AVAILABLE_HOTELS),
        showRefundableHotelRatesOnly:
          checkPreference(newValue, HotelPreferences.ONLY_REFUNDABLE_RATES),
      },
    };
    if (isHotelTripSearch) {
      dispatch(setHotelTripSearchInputs({ ...payload.searchObj }));
    } else {
      dispatch(hotelSearch(payload));
    }
    track('filterPreferences.handleChange', {
      filterPreferences: JSON.stringify(newValue),
    });
  };

  const selectedPreferences = useMemo(() => {
    if (!preferences.length) return [];
    const selectedPref = [];
    if (hotelSearchObj.doShowOnlyAgentPreferredHotels) {
      selectedPref.push(HotelPreferences.ONLY_AGENT_PREFERRED_HOTEL);
    }
    if (hotelSearchObj.exactMatchRateCodesOnly && doShowInPreferences) {
      if (hasRateCodesSelected) {
        selectedPref.push(HotelPreferences.ONLY_PREFERED_PARTNER_RATES);
      }
    } else if (!doShowInPreferences && !hotelSearchObj.exactMatchRateCodesOnly) {
      selectedPref.push(HotelPreferences.BEST_AVAILABLE_RATES);
    }
    if (hotelSearchObj.doShowCommissionableRatesOnly) {
      selectedPref.push(HotelPreferences.ONLY_COMMISSIONABLE);
    }
    if (hotelSearchObj.showRefundableHotelRatesOnly) {
      selectedPref.push(HotelPreferences.ONLY_REFUNDABLE_RATES);
    }
    if (hotelSearchObj.doShowAvailableHotelsOnly) {
      selectedPref.push(HotelPreferences.ONLY_AVAILABLE_HOTELS);
    }
    return selectedPref;
  }, [hotelSearchObj.doShowOnlyAgentPreferredHotels,
    hotelSearchObj.exactMatchRateCodesOnly, hotelSearchObj.showRefundableHotelRatesOnly,
    hotelSearchObj.doShowCommissionableRatesOnly, preferences.length,
    hotelSearchObj.doShowAvailableHotelsOnly, doShowInPreferences, hasRateCodesSelected]);

  const handlePreferencesClear = () => {
    const filteredValues = selectedPreferences && selectedPreferences.length
      ? _.filter(selectedPreferences, (v) => !_.find(preferences, (o) => o.value === v)) : [];
    handlePreferencesChange(filteredValues);
  };

  const isAgentPreferencesEmpty = useMemo(() => {
    if (!selectedPreferences || !selectedPreferences.length
      || !preferences || !preferences.length) return true;

    // to check empty value, consider values which are in options.
    // For some agencies, we have default values which are not in options
    const filteredValues = _.filter(selectedPreferences,
      (v) => !!_.find(preferences, (o) => o.value === v));

    return !filteredValues.length;
  }, [preferences, selectedPreferences]);

  const agentPreferencesLabel = useMemo(() => (isAgentPreferencesEmpty ? 'Preferences' : selectedAgentPreferenceValuesPrefix), [isAgentPreferencesEmpty, selectedAgentPreferenceValuesPrefix]);

  return {
    handlePreferencesChange,
    handlePreferencesClear,
    agentPreferencesLabel,
    selectedAgentPreferenceValuesPrefix,
    isAgentPreferencesEmpty,
    preferences,
    selectedPreferences,
  };
};

export default usePreferencesFilter;
